// import components
import { Button, CircularProgress, Typography } from '@mui/material';
import { ToolbarWrapper } from 'components/shared/ToolbarSet/ToolbarWrapper';
import BacklogCategoryTiles from './components/BacklogCategoryTiles';
import BacklogToolbarLeadingControls from './components/BacklogToolbarLeadingControls';
import BacklogGrid from './components/BacklogGrid';
import BacklogViewSettings from './components/BacklogViewSettings';
import BacklogWeekDetail from './components/BacklogWeekDetail';

// import external libraries
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// import constants
import { BACKLOG_REQUIRED_FILTER_MESSAGES } from './Backlog.consts';

// import hooks
import { useBacklog } from './Backlog.hooks';

// import utils
import { getWeekDays } from './components/BacklogGrid/BacklogGrid.utils';

// import styles
import {
  BacklogContentArea,
  BacklogPageContainer,
  BacklogStateArea,
  BacklogTilesArea,
  BacklogToolbarArea
} from './Backlog.styles';

/** DOM id of the view-settings eye, which its popover reads back to anchor itself under the icon. */
const VIEW_SETTINGS_ID = 'backlog-view-settings';

const Backlog = () => {
  const { t } = useTranslation('common', { keyPrefix: 'backlog' });
  const [isViewSettingsOpen, setIsViewSettingsOpen] = useState(false);

  const {
    selectedDate,
    onDateChange,
    activityOptions,
    selectedActivityOptions,
    onActivityIdsChange,
    entityFilters,
    onEntityFiltersChange,
    categoryCounts,
    activeCategories,
    onToggleCategory,
    onToggleAll,
    onToggleCategoryRow,
    cellValueMode,
    setCellValueMode,
    showTotals,
    setShowTotals,
    showCompleted,
    setShowCompleted,
    rosterDays,
    rows,
    backlog,
    weeks,
    ruleRows,
    rowEntityType,
    onRowEntityTypeChange,
    requiredFilter,
    employeeViewFilters,
    onEmployeeViewFiltersChange,
    startDate,
    endDate,
    collapsedCategoryKeys,
    openWeek,
    onOpenWeek,
    onCloseWeek,
    onPeriodChange,
    isLoading,
    isError,
    refetch
  } = useBacklog();

  const renderContent = () => {
    // Before the loading and error branches: while the view still needs a care receiver or a plan unit the
    // query is never issued, so there is nothing loading and nothing failing — only a question the planner
    // has not finished asking. The message names what to pick.
    if (requiredFilter) {
      return (
        <BacklogStateArea>
          <Typography variant="body2" color="text.secondary">
            {t(BACKLOG_REQUIRED_FILTER_MESSAGES[requiredFilter])}
          </Typography>
        </BacklogStateArea>
      );
    }

    if (isLoading) {
      return (
        <BacklogStateArea>
          <CircularProgress />
        </BacklogStateArea>
      );
    }

    if (isError) {
      return (
        <BacklogStateArea>
          <Typography variant="body2" color="text.secondary">
            {t('loadError')}
          </Typography>
          <Button variant="outlined" onClick={() => refetch()}>
            {t('retry')}
          </Button>
        </BacklogStateArea>
      );
    }

    // The week-detail is a drill-down of the same data, so it takes over the content area when a week
    // is open — its own back breadcrumb returns to the weeks grid.
    if (openWeek) {
      return (
        <BacklogWeekDetail
          weekDays={getWeekDays(weeks, openWeek)}
          weekField={openWeek}
          backlog={backlog}
          ruleRows={ruleRows}
          activeCategories={activeCategories}
          cellValueMode={cellValueMode}
          collapsedCategoryKeys={collapsedCategoryKeys}
          onToggleCategoryRow={onToggleCategoryRow}
          onClose={onCloseWeek}
        />
      );
    }

    if (activeCategories.length === 0) {
      return (
        <BacklogStateArea>
          <Typography variant="body2" color="text.secondary">
            {t('noCategorySelected')}
          </Typography>
        </BacklogStateArea>
      );
    }

    if (rows.length === 0) {
      return (
        <BacklogStateArea>
          <Typography variant="body2" color="text.secondary">
            {t('noDeviations')}
          </Typography>
        </BacklogStateArea>
      );
    }

    return (
      <BacklogGrid
        rows={rows}
        rosterDays={rosterDays}
        cellValueMode={cellValueMode}
        showTotals={showTotals}
        onToggleCategoryRow={onToggleCategoryRow}
        onOpenWeek={onOpenWeek}
      />
    );
  };

  return (
    <BacklogPageContainer>
      <BacklogTilesArea>
        <BacklogCategoryTiles
          categoryCounts={categoryCounts}
          activeCategories={activeCategories}
          onToggleCategory={onToggleCategory}
          onToggleAll={onToggleAll}
        />
      </BacklogTilesArea>

      <BacklogToolbarArea>
        <ToolbarWrapper
          // Default GRID flow: both toolbar controls sit in the left area (before the date picker), the
          // date picker keeps the centre column, and the right area is unused.
          dateNavigation={{
            defaultPeriod: 'eightWeeks',
            defaultDate: selectedDate,
            onDateChange,
            // The 8-weken/week choice lives in the date-picker menu, so it stays reachable when the
            // toolbar is docked to the right. Picking 'week' opens the week-detail; 'eightWeeks' the grid.
            options: ['eightWeeks', 'week'],
            allowMenuOptions: true,
            onPeriodChange,
            // The eightWeeks/week switch is this view's own drill-down, not an app-wide preference, so it
            // must not write `defaultDateNavigationPeriod` (which would leak 'week' into the roster and
            // other date-navigated views, and reopen the takenlijst straight into the week-detail).
            persistPeriodPreference: false,
            // The period name would read "8 weken"/"Week" before the date; the menu already says which,
            // so only the date itself is shown.
            hidePeriodDescription: true
          }}
          // The view settings sit at the far end of the toolbar, immediately before its own overflow
          // menu: they change how the grid is drawn rather than what it lists, so they belong with the
          // toolbar's own controls rather than beside the filters. Only the icon is an action — its
          // popover is injected in the slot below, which is what tells it how the toolbar is docked.
          actions={[
            {
              id: VIEW_SETTINGS_ID,
              icon: 'eye',
              tooltipLabel: t('viewSettings'),
              ariaLabel: t('viewSettings'),
              onClick: () => setIsViewSettingsOpen((open) => !open),
              alignment: 'right'
            }
          ]}
          component={{
            // The entity symbols and the filters (sliders) icon sit on the left, before the date
            // picker: which entity's list you are on, and what narrows it.
            beforeDatePicker: (
              <BacklogToolbarLeadingControls
                rowEntityType={rowEntityType}
                onRowEntityTypeChange={onRowEntityTypeChange}
                activityOptions={activityOptions}
                selectedActivityOptions={selectedActivityOptions}
                onActivityIdsChange={onActivityIdsChange}
                entityFilters={entityFilters}
                onEntityFiltersChange={onEntityFiltersChange}
                employeeViewFilters={employeeViewFilters}
                onEmployeeViewFiltersChange={onEmployeeViewFiltersChange}
                startDate={startDate}
                endDate={endDate}
              />
            ),
            afterDatePicker: (
              <BacklogViewSettings
                cellValueMode={cellValueMode}
                onCellValueModeChange={setCellValueMode}
                showTotals={showTotals}
                onShowTotalsChange={setShowTotals}
                showCompleted={showCompleted}
                onShowCompletedChange={setShowCompleted}
                isOpen={isViewSettingsOpen}
                onClose={() => setIsViewSettingsOpen(false)}
                anchorId={VIEW_SETTINGS_ID}
              />
            )
          }}
        >
          <BacklogContentArea>{renderContent()}</BacklogContentArea>
        </ToolbarWrapper>
      </BacklogToolbarArea>
    </BacklogPageContainer>
  );
};

export default Backlog;
